import { Search, X } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { IdentityHeader } from '@/src/components/IdentityHeader';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import type { UserProfileSearchResult } from '@/src/features/profile/profile-service';
import {
  useFollowState,
  useToggleFollow,
  useUserProfileSearch,
} from '@/src/features/profile/use-profile';
import { trackEvent } from '@/src/lib/analytics';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { openUserProfile } from '@/src/utils/profile-navigation';

const SEARCH_DEBOUNCE_MS = 250;

function SearchResultRow({
  currentUid,
  onPress,
  profile,
}: {
  currentUid?: string | null;
  onPress: () => void;
  profile: UserProfileSearchResult;
}) {
  const followState = useFollowState(currentUid ?? undefined, profile.uid);
  const toggleFollow = useToggleFollow(currentUid ?? undefined, profile.uid);
  const { showError, showSuccess } = useToast();
  const canFollow = Boolean(currentUid) && currentUid !== profile.uid;
  const followActionPending = followState.loading || toggleFollow.isPending;

  const followProfile = () => {
    if (followActionPending) {
      return;
    }

    trackEvent('follow_toggle', {
      following: true,
      result: 'started',
      source: 'user_search',
    });

    toggleFollow.mutate(
      { following: true },
      {
        onSuccess: (result) => {
          if (result === 'queued') {
            showSuccess('Follow queued.');
            return;
          }

          showSuccess(`You are now following ${profile.fullName}.`);
        },
        onError: (error) => {
          showError(error, 'Unable to follow this profile.');
        },
      },
    );
  };

  return (
    <Pressable
      accessibilityLabel={`Open ${profile.fullName}`}
      accessibilityRole="button"
      onPress={() => {
        trackEvent('profile_open', {
          source: 'user_search',
          target_is_current_user: profile.uid === currentUid,
        });
        onPress();
        openUserProfile({ currentUid, targetUid: profile.uid });
      }}
      style={({ pressed }) => [
        styles.resultRow,
        pressed ? styles.resultRowPressed : null,
      ]}
    >
      <IdentityHeader
        avatarSize={36}
        containerStyle={styles.resultIdentity}
        currentUid={currentUid}
        density="compact"
        founderBadge={profile.founderBadge}
        fullName={profile.fullName}
        interactive={false}
        photoURL={profile.photoURL}
        uid={profile.uid}
        username={profile.username}
        verifiedBadge={profile.verifiedBadge}
      />
      {!canFollow ? (
        <View style={styles.youPill}>
          <Text style={styles.youPillText}>You</Text>
        </View>
      ) : followActionPending ? (
        <View style={styles.followLoading}>
          <ActivityIndicator color={colors.primary} size="small" />
        </View>
      ) : followState.following ? (
        <View style={[styles.followButton, styles.followingButton]}>
          <Text style={[styles.followButtonText, styles.followingButtonText]}>
            Following
          </Text>
        </View>
      ) : (
        <Pressable
          accessibilityLabel={`Follow ${profile.fullName}`}
          accessibilityRole="button"
          onPress={(event) => {
            event.stopPropagation();
            followProfile();
          }}
          style={({ pressed }) => [
            styles.followButton,
            styles.followButtonPrimary,
            pressed ? styles.followButtonPrimaryPressed : null,
          ]}
        >
          <Text style={styles.followButtonText}>Follow</Text>
        </Pressable>
      )}
    </Pressable>
  );
}

export function UserSearchModal({
  onClose,
  visible,
}: {
  onClose: () => void;
  visible: boolean;
}) {
  const { user } = useAuth();
  const inputRef = useRef<TextInput>(null);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const trackedSearchKeyRef = useRef<string | null>(null);
  const searchableText = debouncedSearchText.trim().replace(/^@/, '');
  const trimmedSearchText = debouncedSearchText.trim();
  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedSearchText);
  const canSearch =
    searchableText.length >= 2 ||
    looksLikeEmail;
  const searchQuery = useUserProfileSearch({
    enabled: visible && canSearch,
    searchText: debouncedSearchText,
  });

  useEffect(() => {
    if (!visible) {
      setSearchText('');
      setDebouncedSearchText('');
      trackedSearchKeyRef.current = null;
      return undefined;
    }

    trackEvent('user_search_open', { source: 'global_header' });

    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);

    return () => clearTimeout(focusTimer);
  }, [visible]);

  useEffect(() => {
    const searchTimer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(searchTimer);
  }, [searchText]);

  const results = searchQuery.data ?? [];

  useEffect(() => {
    if (!visible || !canSearch || !searchQuery.isSuccess) {
      return;
    }

    const searchKey = `${trimmedSearchText}:${searchQuery.dataUpdatedAt}`;

    if (trackedSearchKeyRef.current === searchKey) {
      return;
    }

    trackedSearchKeyRef.current = searchKey;
    trackEvent('user_search', {
      query_length: searchableText.length,
      query_type: looksLikeEmail
        ? 'email'
        : trimmedSearchText.startsWith('@')
          ? 'username'
          : 'name',
      result_count: results.length,
      source: 'global_header',
    });
  }, [
    canSearch,
    looksLikeEmail,
    results.length,
    searchQuery.dataUpdatedAt,
    searchQuery.isSuccess,
    searchableText.length,
    trimmedSearchText,
    visible,
  ]);

  return (
    <Modal
      animationType="fade"
      transparent
      onRequestClose={onClose}
      presentationStyle="overFullScreen"
      visible={visible}
    >
      <View style={styles.overlay}>
        <Pressable
          accessibilityLabel="Close search"
          accessibilityRole="button"
          onPress={onClose}
          style={StyleSheet.absoluteFill}
        />
        <SafeAreaView pointerEvents="box-none" style={styles.safeArea}>
          <View style={styles.sheet}>
            <View style={styles.header}>
              <View style={styles.searchBox}>
                <Search color={colors.mutedText} size={20} strokeWidth={2.4} />
                <TextInput
                  ref={inputRef}
                  autoCapitalize="none"
                  autoCorrect={false}
                  clearButtonMode="while-editing"
                  onChangeText={setSearchText}
                  placeholder="Name, username, or email"
                  placeholderTextColor={colors.mutedText}
                  returnKeyType="search"
                  style={styles.searchInput}
                  value={searchText}
                />
              </View>
              <Pressable
                accessibilityLabel="Close search"
                accessibilityRole="button"
                onPress={onClose}
                style={({ pressed }) => [
                  styles.closeButton,
                  pressed ? styles.iconButtonPressed : null,
                ]}
              >
                <X color={colors.text} size={22} strokeWidth={2.4} />
              </Pressable>
            </View>

            <FlatList
              keyboardShouldPersistTaps="handled"
              data={results}
              keyExtractor={(item) => item.uid}
              contentContainerStyle={[
                styles.resultsContent,
                results.length === 0 ? styles.resultsContentEmpty : null,
              ]}
              renderItem={({ item }) => (
                <SearchResultRow
                  currentUid={user?.uid}
                  profile={item}
                  onPress={onClose}
                />
              )}
              ItemSeparatorComponent={() => (
                <View style={styles.resultSeparator} />
              )}
              ListEmptyComponent={
                <View style={styles.emptyState}>
                  <Text style={styles.emptyTitle}>
                    {!canSearch
                      ? 'Search Frienzzi'
                      : searchQuery.isFetching
                        ? 'Searching...'
                        : searchQuery.isError
                          ? 'Search is unavailable'
                          : trimmedSearchText
                            ? 'No people found'
                            : 'Search Frienzzi'}
                  </Text>
                  <Text style={styles.emptyText}>
                    {!canSearch
                      ? 'Name, username, or email'
                      : searchQuery.isError
                        ? 'Check your connection and try again.'
                        : 'Try another spelling or email address.'}
                  </Text>
                </View>
              }
            />
          </View>
        </SafeAreaView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(26, 12, 62, 0.36)',
  },
  safeArea: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.sm,
  },
  sheet: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 16,
    borderWidth: 1,
    maxHeight: 560,
    maxWidth: 560,
    minHeight: 320,
    overflow: 'hidden',
    shadowColor: colors.text,
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.18,
    shadowRadius: 28,
    width: '100%',
  },
  header: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderBottomColor: colors.border,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  searchBox: {
    alignItems: 'center',
    backgroundColor: colors.background,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flex: 1,
    flexDirection: 'row',
    gap: spacing.sm,
    minHeight: 44,
    paddingHorizontal: spacing.md,
  },
  searchInput: {
    color: colors.text,
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    minHeight: 42,
    outlineStyle: 'none' as never,
  },
  closeButton: {
    alignItems: 'center',
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  iconButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  resultsContent: {
    paddingVertical: spacing.sm,
  },
  resultsContentEmpty: {
    minHeight: 230,
  },
  resultRow: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    flexDirection: 'row',
    gap: spacing.md,
    minHeight: 72,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  resultRowPressed: {
    backgroundColor: colors.surfacePressed,
  },
  resultIdentity: {
    flex: 1,
  },
  followButton: {
    alignItems: 'center',
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: 'center',
    minHeight: 34,
    minWidth: 94,
    paddingHorizontal: spacing.md,
    paddingVertical: 7,
  },
  followButtonDisabled: {
    opacity: 0.55,
  },
  followButtonPrimary: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  followButtonPrimaryPressed: {
    backgroundColor: colors.primaryPressed,
    borderColor: colors.primaryPressed,
  },
  followButtonText: {
    color: colors.surface,
    fontSize: 13,
    fontWeight: '900',
  },
  followingButton: {
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
  },
  followingButtonText: {
    color: colors.secondary,
  },
  followLoading: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 34,
    minWidth: 94,
  },
  youPill: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    justifyContent: 'center',
    minHeight: 34,
    minWidth: 94,
    paddingHorizontal: spacing.md,
    paddingVertical: 7,
  },
  youPillText: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
  },
  resultSeparator: {
    backgroundColor: colors.border,
    height: StyleSheet.hairlineWidth,
    marginLeft: spacing.lg + 48 + spacing.md,
  },
  emptyState: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.xl,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 22,
    fontWeight: '900',
    lineHeight: 28,
    textAlign: 'center',
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 15,
    fontWeight: '700',
    lineHeight: 22,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
});
